import { Component, Inject, OnInit, Input } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { SpinnerService } from 'src/app/core/services/spinner.service';
import { AdminService } from 'src/app/core/services/admin.service';
import { MSTeamsSharedService } from 'src/app/core/services/msteams-shared.service';
import { FeatureToggleService } from 'src/app/core/services/feature-toggle.service';
import { FeatureFlags } from 'src/app/core/interfaces/feature-flags';

@Component({
  selector: 'app-connector-config-dialog',
  templateUrl: './connector-config-dialog.component.html',
  styleUrls: ['./connector-config-dialog.component.scss']
})
export class ConnectorConfigDialogComponent implements OnInit {
	configForm = this.formBuilder.group({
		name: ['', []],
		tenantId: ['', []],
		clientId: ['', []],
		clientSecret: ['', []]
	});
	name: string = '';
	tenantId: string = '';
	clientId: string = '';
	clientSecret: string = '';
	connectionData: any = {};
	previousConnectionData: any = {};
	affectsMonitors: boolean = false;
	monitorsExist: boolean = false;

	showFormError: boolean = false;
	errMsg: string = "";
	errMsgStyle: any = { 'max-width': '500px' };

	constructor( @Inject(MAT_DIALOG_DATA) public data: any,
				public dialogRef: MatDialogRef<ConnectorConfigDialogComponent>,
				private translateService: TranslateService,
				private domSanitizer: DomSanitizer,
				private matIconRegistry: MatIconRegistry,
				private formBuilder: FormBuilder,
				private adminService: AdminService,
				private featureToggleService: FeatureToggleService,
				private msteamsSharedService: MSTeamsSharedService,
				private spinnerService: SpinnerService ) 
	{
		this.matIconRegistry.addSvgIcon(
			'trash_can',
			this.domSanitizer.bypassSecurityTrustResourceUrl("../assets/images/trash.svg")
		);
	}

	async ngOnInit() 
	{
		let product = this.data.product;
		return product.service.getConnectorData(this.data.account).then(async connectorData => {
			this.name = connectorData.name;
			this.tenantId = connectorData.tenantId;
			this.clientId = connectorData.clientId;
			this.clientSecret = connectorData.clientSecret;
			this.connectionData = connectorData;
			this.previousConnectionData = Object.assign({}, connectorData);

			if ( connectorData.connectionId != undefined )
			{
				let isExpired = false;

				try
				{
					isExpired = await this.msteamsSharedService.isMainConnectorClientSecretExpired( connectorData );
					if ( isExpired ) 
					{
						this.errMsg = this.translateService.instant('integrations.generic-connector-dialog.error-client-secret-expired');
						this.showFormError = true;
					}
				}
				catch (err)
				{
					let missingPermission = this.msteamsSharedService.checkForMissingWFGraphPermission(err);
					if ( missingPermission == true )
					{
						this.errMsg = this.translateService.instant('integrations.generic-connector-dialog.error-missing-wf-graph-permission');
						this.showFormError = true;
					}
					// at this point we do not know the impact of other errors so they are ignored
					// the important ones are covered. 
				}

				if ( isExpired == false && connectorData.connected == false )
				{
					this.errMsg = this.translateService.instant('integrations.msteams.connector-error-reason');
					this.showFormError = true;
				}

				// we need to determine if there are preexisting presence monitors
				return this.adminService.getAccountTags( this.data.account.accountId ).then( tags => {
					if ( tags && tags.MSTeamsToCloudLink && tags.MSTeamsToCloudLink.presenceMonitors && tags.MSTeamsToCloudLink.presenceMonitors.length > 0 )
					{
						this.monitorsExist = true;
					}
				})
			}
		})
		.catch (err => {
			this.displayErrorBasedOnException( err, false );
			this.spinnerService.hideSpinner();
		})
	}

	testMonitorAffectingChange(event)
	{
		if ( this.monitorsExist == false )
			return;

		let testTenantId = this.tenantId?.trim();
		let testClientId = this.clientId?.trim();
		if ( (this.previousConnectionData.tenantId != testTenantId) ||
			(this.previousConnectionData.clientId != testClientId) )
		{
			this.affectsMonitors = true;
		} 
		else
		{
			this.affectsMonitors = false;
		}
	}

	onRemoveIntegration() {
		console.log("Remove the integration - when successful, close the dialog");
		let result =
		{
			remove: true
		}
		this.dialogRef.close( result );
	}

	onSave(event) {
		console.log("Save the changes to the integration - when successful, close the dialog");
		this.showFormError = false;
		let product = this.data.product;
		this.spinnerService.setSpinnerMessageByKey('spinnerMessages.updatingAccount');
		this.spinnerService.showSpinner();

		// remove any leading and trailing spaces
		if (this.name)
			this.name = this.name.trim();
		if (this.tenantId)
			this.tenantId = this.tenantId.trim();
		if (this.clientId)
			this.clientId = this.clientId.trim();
		if (this.clientSecret)
			this.clientSecret = this.clientSecret.trim();

		this.connectionData.name = this.name;	
		this.connectionData.tenantId = this.tenantId;
		this.connectionData.clientId = this.clientId;
		this.connectionData.clientSecret = this.clientSecret;
		return product.service.saveConnectorData( this.data.account, this.connectionData ).then(res => {
			let result =
			{
				setupComplete: true,
			}
			this.dialogRef.close(result);

			this.spinnerService.hideSpinner();
		})
		.catch( err => {
			this.displayErrorBasedOnException( err, true );
			this.spinnerService.hideSpinner();
		});
	}

	onCancel(event) {
		this.dialogRef.close( );
	}

	displayErrorBasedOnException( err: any, onSave: boolean ) {
		let theMessage = '';
		console.error("Display this error to the user", err);
		if ( onSave == true ) 
			this.errMsg = this.translateService.instant('integrations.generic-connector-dialog.error-saving-generic-connector-config');
		else
			this.errMsg = this.translateService.instant('integrations.generic-connector-dialog.error-retrieving-generic-connector-config');

	 	if ( err.body && err.body.message ) {
			theMessage = err.body.message;
		}
		else if ( err.error && err.error.message ) {
			theMessage = err.error.message;
		}
		else // just show the generic message
		{
			this.showFormError = true;
			return;
		}

		// concatenate the generic and specific message
		if ( theMessage != undefined || theMessage.length > 0 )
			this.errMsg = this.errMsg + ": " + theMessage;
		this.showFormError = true;

	}
}
